import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../../services/AxiosInstance";

/* ✅ Fetch ALL receipts (if you still want it somewhere) */
export const fetchReceiptsByHouseThunk = createAsyncThunk(
  "receipts/fetchByHouse",
  async (home_id, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/api/receipts/house/${home_id}`);
      if (!data?.success) return rejectWithValue(data.message || "Failed");
      return data.data;
    } catch (e) {
      return rejectWithValue(e?.response?.data?.message || e?.message || "Server error");
    }
  }
);

/* ✅ NEW: Fetch WATER receipts only */
export const fetchWaterReceiptsByHouseThunk = createAsyncThunk(
  "receipts/fetchWaterByHouse",
  async (home_id, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/api/receipts/house/${home_id}/water`);
      if (!data?.success) return rejectWithValue(data.message || "Failed");
      return data.data;
    } catch (e) {
      return rejectWithValue(e?.response?.data?.message || e?.message || "Server error");
    }
  }
);

/* ✅ NEW: Fetch SLRM receipts only */
export const fetchSlrmReceiptsByHouseThunk = createAsyncThunk(
  "receipts/fetchSlrmByHouse",
  async (home_id, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/api/receipts/house/${home_id}/slrm`);
      if (!data?.success) return rejectWithValue(data.message || "Failed");
      return data.data;
    } catch (e) {
      return rejectWithValue(e?.response?.data?.message || e?.message || "Server error");
    }
  }
);

/* Create receipt */
export const createReceiptThunk = createAsyncThunk(
  "receipts/create",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`/api/receipts`, payload);
      if (!data?.success) return rejectWithValue(data.message || "Failed");
      return true;
    } catch (e) {
      return rejectWithValue(e?.response?.data?.message || e?.message || "Server error");
    }
  }
);

/* Delete receipt */
export const deleteReceiptThunk = createAsyncThunk(
  "receipts/delete",
  async ({ rct_no, home_id }, { rejectWithValue }) => {
    try {
      const { data } = await api.delete(`/api/receipts/${rct_no}/${home_id}`);
      if (!data?.success) return rejectWithValue(data.message || "Failed");
      return rct_no;
    } catch (e) {
      return rejectWithValue(e?.response?.data?.message || e?.message || "Server error");
    }
  }
);

/* ✅ PRINT receipt PDF (BLOB) - FIXED URL */
export const fetchPrintReceiptPdfThunk = createAsyncThunk(
  "receipts/fetchPrintReceiptPdf",
  async (rct_no, { rejectWithValue }) => {
    try {
      const res = await api.get(`/api/receipts/print_receipt_pdf/${rct_no}`, {
        responseType: "blob",
      });

      const contentType = res.headers?.["content-type"] || "";

      if (!contentType.includes("application/pdf")) {
        let msg = "Failed to generate PDF";
        try {
          const text = await res.data.text();
          if (text) msg = text;
        } catch (_) {}
        return rejectWithValue(msg);
      }

      const pdfUrl = window.URL.createObjectURL(res.data);
      return pdfUrl;
    } catch (e) {
      return rejectWithValue(e?.response?.data?.message || e?.message || "Server error");
    }
  }
);

/* ✅ PRINT receipt DATA (JSON) */
export const fetchPrintReceiptDataThunk = createAsyncThunk(
  "receipts/fetchPrintReceiptData",
  async (rct_no, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/api/receipts/print_receipt_data/${rct_no}`);
      if (!data?.success) return rejectWithValue(data?.message || "Failed to load receipt");
      return data.data; // { receipt, user, settings }
    } catch (e) {
      return rejectWithValue(e?.response?.data?.message || e?.message || "Server error");
    }
  }
);

export const createSlrmReceiptThunk = createAsyncThunk(
  "receipts/createSlrm",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`/api/receipts/slrm`, payload);
      if (!data?.success) return rejectWithValue(data?.message || "Failed");
      return data; // { success:true, data:{rct_no} }
    } catch (e) {
      return rejectWithValue(e?.response?.data?.message || e?.message || "Server error");
    }
  }
);

const slice = createSlice({
  name: "receipts",
  initialState: {
    list: [],
    loading: false,
    saving: false,
    deleting: null,
    error: null,

    printLoading: false,
    printError: null,
    printPdfUrl: null,

    printData: null,
  },
  reducers: {
    clearReceiptsError: (s) => {
      s.error = null;
    },

    clearPrintReceipt: (s) => {
      s.printError = null;
      s.printLoading = false;

      if (s.printPdfUrl) {
        try {
          window.URL.revokeObjectURL(s.printPdfUrl);
        } catch (_) {}
      }
      s.printPdfUrl = null;
      s.printData = null;
    },
  },
  extraReducers: (b) => {
    b
      // ---------------- fetch ALL ----------------
      .addCase(fetchReceiptsByHouseThunk.pending, (s) => {
        s.loading = true;
        s.error = null;
      })
      .addCase(fetchReceiptsByHouseThunk.fulfilled, (s, a) => {
        s.loading = false;
        s.list = a.payload || [];
      })
      .addCase(fetchReceiptsByHouseThunk.rejected, (s, a) => {
        s.loading = false;
        s.error = a.payload || "Failed";
      })

      // ---------------- ✅ fetch WATER ----------------
      .addCase(fetchWaterReceiptsByHouseThunk.pending, (s) => {
        s.loading = true;
        s.error = null;
      })
      .addCase(fetchWaterReceiptsByHouseThunk.fulfilled, (s, a) => {
        s.loading = false;
        s.list = a.payload || [];
      })
      .addCase(fetchWaterReceiptsByHouseThunk.rejected, (s, a) => {
        s.loading = false;
        s.error = a.payload || "Failed";
      })

      // ---------------- ✅ fetch SLRM ----------------
      .addCase(fetchSlrmReceiptsByHouseThunk.pending, (s) => {
        s.loading = true;
        s.error = null;
      })
      .addCase(fetchSlrmReceiptsByHouseThunk.fulfilled, (s, a) => {
        s.loading = false;
        s.list = a.payload || [];
      })
      .addCase(fetchSlrmReceiptsByHouseThunk.rejected, (s, a) => {
        s.loading = false;
        s.error = a.payload || "Failed";
      })

      // ---------------- create ----------------
      .addCase(createReceiptThunk.pending, (s) => {
        s.saving = true;
        s.error = null;
      })
      .addCase(createReceiptThunk.fulfilled, (s) => {
        s.saving = false;
      })
      .addCase(createReceiptThunk.rejected, (s, a) => {
        s.saving = false;
        s.error = a.payload || "Failed";
      })

      // ---------------- delete ----------------
      .addCase(deleteReceiptThunk.pending, (s, a) => {
        s.deleting = a.meta.arg.rct_no;
        s.error = null;
      })
      .addCase(deleteReceiptThunk.fulfilled, (s, a) => {
        s.deleting = null;
        s.list = s.list.filter((x) => x.rct_no !== a.payload);
      })
      .addCase(deleteReceiptThunk.rejected, (s, a) => {
        s.deleting = null;
        s.error = a.payload || "Failed";
      })

      // ---------------- print pdf ----------------
      .addCase(fetchPrintReceiptPdfThunk.pending, (s) => {
        s.printLoading = true;
        s.printError = null;

        if (s.printPdfUrl) {
          try {
            window.URL.revokeObjectURL(s.printPdfUrl);
          } catch (_) {}
        }
        s.printPdfUrl = null;
        s.printData = null;
      })
      .addCase(fetchPrintReceiptPdfThunk.fulfilled, (s, a) => {
        s.printLoading = false;
        s.printPdfUrl = a.payload || null;
      })
      .addCase(fetchPrintReceiptPdfThunk.rejected, (s, a) => {
        s.printLoading = false;
        s.printError = a.payload || "Failed to print";
      })

      // ---------------- print json ----------------
      .addCase(fetchPrintReceiptDataThunk.pending, (s) => {
        s.printLoading = true;
        s.printError = null;

        s.printData = null;

        if (s.printPdfUrl) {
          try {
            window.URL.revokeObjectURL(s.printPdfUrl);
          } catch (_) {}
        }
        s.printPdfUrl = null;
      })
      .addCase(fetchPrintReceiptDataThunk.fulfilled, (s, a) => {
        s.printLoading = false;
        s.printData = a.payload || null;
      })
      .addCase(fetchPrintReceiptDataThunk.rejected, (s, a) => {
        s.printLoading = false;
        s.printError = a.payload || "Failed to load receipt";
      })

      // ---------------- create slrm ----------------
      .addCase(createSlrmReceiptThunk.pending, (s) => {
        s.saving = true;
        s.error = null;
      })
      .addCase(createSlrmReceiptThunk.fulfilled, (s) => {
        s.saving = false;
      })
      .addCase(createSlrmReceiptThunk.rejected, (s, a) => {
        s.saving = false;
        s.error = a.payload || "Failed";
      });
  },
});

export const { clearReceiptsError, clearPrintReceipt } = slice.actions;
export default slice.reducer;